import { Dispatch } from 'redux'

import { useThunkActions } from 'redux/actions/useThunkActions'
import {
  addKeyMetric,
  addMetric,
  removeKeyMetric,
  removeMetric,
  create,
  destroy,
  getAll,
  invite,
  update,
  TPostGroupMetric
} from 'redux/api/Groups'

import * as actions from './actions'
import { CreateGroupBody } from 'types/GlobalGroup'
import { toggleSnackbarOpen } from 'redux/actions/Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'

export function tryGetAllGroups(dispatch: Dispatch) {
  dispatch(actions.getAll())

  getAll()
    .then((response) => {
      dispatch(actions.getAllSuccess(response.data))
    })
    .catch(() => {
      dispatch(actions.getAllFailed())
    })
}

function tryCreateGroup(
  body: CreateGroupBody,
  cb: (err: true | null, id: string | null) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create(body)
      .then((response) => {
        dispatch(actions.createSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupCreated))

        cb(null, response.data.data.id)
      })
      .catch(() => {
        dispatch(actions.createFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupCreatedFailed))

        cb(true, null)
      })
  }
}

function tryUpdateGroup(
  id: string,
  body: Partial<CreateGroupBody>,
  cb: (err?: true) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, body)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdated))

        cb()
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdatedFailed))

        cb(true)
      })
  }
}

/**
 *
 * @param id - Id of group
 * @param accounts - account id for self service, or list of bulk invite.
 * @param isPrivate - if group is private (only applicable for self-service)
 */
function tryInviteToGroup(
  id: string,
  accounts: string | string[],
  isPrivate: boolean,
  notify: boolean
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    const isSelfService = typeof accounts === 'string'
    const toInvite = isSelfService ? [accounts] : accounts

    invite(id, toInvite, notify)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data))

        const message = isPrivate
          ? SnackbarMessage.AccessRequested
          : isSelfService
            ? SnackbarMessage.YouJoinedGroup
            : SnackbarMessage.GroupUpdated

        dispatch(toggleSnackbarOpen(message))
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        const message = isPrivate
          ? SnackbarMessage.AccessRequestedFailed
          : isSelfService
            ? SnackbarMessage.YouJoinedGroupFailed
            : SnackbarMessage.GroupUpdatedFailed

        dispatch(toggleSnackbarOpen(message))
      })
  }
}

function tryDeleteGroup(id: string, cb: (err?: true) => void) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess(id))

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupDeleted))

        cb()
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupDeletedFailed))

        cb(true)
      })
  }
}

function tryAddKeyMetricToGroup(id: string, keyMetricId: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.addKeyMetric())

    addKeyMetric(id, keyMetricId)
      .then((response) => {
        dispatch(actions.addKeyMetricSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdated))
      })
      .catch(() => {
        dispatch(actions.addKeyMetricFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdatedFailed))
      })
  }
}

function tryAddMetricToGroup(
  id: string,
  data: TPostGroupMetric,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.addMetric())

    addMetric(id, data)
      .then((response) => {
        dispatch(actions.addMetricSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.addMetricFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdatedFailed))

        callback?.(true)
      })
  }
}

function tryRemoveKeyMetricFromGroup(id: string, keyMetricId: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.removeKeyMetric())

    removeKeyMetric(id, keyMetricId)
      .then((response) => {
        dispatch(actions.removeKeyMetricSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdated))
      })
      .catch(() => {
        dispatch(actions.removeKeyMetricFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdatedFailed))
      })
  }
}

function tryRemoveMetricFromGroup(id: string, groupMetricId: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.removeMetric())

    removeMetric(id, groupMetricId)
      .then((response) => {
        dispatch(actions.removeMetricSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdated))
      })
      .catch(() => {
        dispatch(actions.removeMetricFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.GroupUpdatedFailed))
      })
  }
}

export const useGroupActions = () => {
  return {
    tryCreateGroup: useThunkActions(tryCreateGroup),
    tryUpdateGroup: useThunkActions(tryUpdateGroup),
    tryDeleteGroup: useThunkActions(tryDeleteGroup),
    tryInviteToGroup: useThunkActions(tryInviteToGroup),
    tryAddKeyMetricToGroup: useThunkActions(tryAddKeyMetricToGroup),
    tryAddMetricToGroup: useThunkActions(tryAddMetricToGroup),
    tryRemoveKeyMetricFromGroup: useThunkActions(tryRemoveKeyMetricFromGroup),
    tryRemoveMetricFromGroup: useThunkActions(tryRemoveMetricFromGroup)
  }
}
